import type { Maturity } from '@tunasong/models'
import { shortUuid } from '@tunasong/models'
import type { TunaEditor, TunaEditorConfig, TunaPlugin } from '@tunasong/plugin-lib'
import { maturityFilter, useGlobalAppContext, usePlugins } from '@tunasong/plugin-lib'
import type { RootState } from '@tunasong/redux'
import { useThunkDispatch } from '@tunasong/redux'
import type { CoreElement, EntityOrElement } from '@tunasong/schemas'
import { isCoreElement } from '@tunasong/schemas'
import { useUserConfig } from '@tunasong/ui-lib'
import type { PlatePlugin } from '@platejs/core/react'
import { createPlateEditor, createPlatePlugin, withPlate } from '@platejs/core/react'
import { useEffect, useMemo, useState } from 'react'
import { useStore } from 'react-redux'
import type { NodeEntry } from 'slate'
import { Text } from 'slate'
import type { Descendant, NodeIn, Value, Operation } from 'platejs'
import { installSelectionStore } from './selection-store.js'

/**
 * Live-editor leak gauge (GH #1666).
 *
 * Performance mode remounts the editor on every song switch (the editor is
 * keyed by entity id). A per-remount retention keeps the *whole* Slate/Plate
 * editor — and its per-plugin option stores — alive after unmount, growing the
 * heap ~0.9MB/round (heap-snapshot evidence: live editors 4→34 over 90 remounts
 * on the production build). Tunasong's own editor-scoped subscriptions are all
 * cleaned up; the residual root is inside platejs 52's per-editor store
 * machinery, so the complete fix is framework-level.
 *
 * Until that lands, this gauge makes the leak observable and gate-able: a
 * `FinalizationRegistry` decrements on GC, so editors that are properly
 * collected stop counting while *retained* ones never do. The perf memory gate
 * reads `live` after a forced GC and asserts it stays bounded across remounts —
 * a far sharper, lower-variance signal than total heap MB. Cost is ~zero (one
 * register call per editor); the held value is `undefined`, never the editor,
 * so the gauge itself introduces no retention.
 */
let editorsCreated = 0
let editorsFinalized = 0
const editorFinalizationRegistry =
  typeof FinalizationRegistry !== 'undefined' ? new FinalizationRegistry<void>(() => void editorsFinalized++) : null

export interface EditorLiveStats {
  /** Editors constructed since page load (monotonic). */
  created: number
  /** Editors observed collected by GC (lags collection, monotonic). */
  finalized: number
  /** `created - finalized` — retained + not-yet-collected. After a forced GC this is the live/retained set. */
  live: number
}

/** Snapshot of the live-editor gauge. Exposed on `globalThis.__tunaEditorStats` for the perf memory gate. */
export const getEditorLiveStats = (): EditorLiveStats => ({
  created: editorsCreated,
  finalized: editorsFinalized,
  live: editorsCreated - editorsFinalized,
})

// Expose for the Playwright perf memory gate (GH #1666). Read-only diagnostic;
// safe to ship in production (the test runs against the preview/production build).
// eslint-disable-next-line no-underscore-dangle
;(globalThis as unknown as { __tunaEditorStats?: typeof getEditorLiveStats }).__tunaEditorStats = getEditorLiveStats

/** Create the (raw) editor. If you manage this you need to handle the root properties (e.g., id) separately. In the normal case, use `useCreateEditor` instead */

export const createTunaEditor = <T extends TunaEditor = TunaEditor>(
  id: string,
  plugins: TunaPlugin[],
  config: TunaEditorConfig,
  maturity: Maturity
) => {
  const ed = createPlateEditor<CoreElement[]>()

  /** Save Slate's base normalizeNode before plugins wrap it. Used below to
   *  skip the full plugin chain for plain text nodes. */
  const baseNormalizeNode = ed.normalizeNode as (entry: NodeEntry, options?: { operation?: unknown }) => void

  /** corePlugins are included with Plate, filtered by maturity */
  const filteredPlugins = plugins.filter(maturityFilter(maturity))

  /** @todo do this in the plugins instead? */
  const preparePlugin = (p: TunaPlugin) => {
    const subPlugins = p.plugins?.map(p => createPlatePlugin(p as unknown as PlatePlugin)) ?? []
    return createPlatePlugin({ ...p, plugins: subPlugins } as unknown as PlatePlugin)
  }
  const platePlugins = filteredPlugins.map(preparePlugin)

  const editor = withPlate(ed, {
    id,
    plugins: platePlugins,
    nodeId: {
      idCreator: shortUuid,
      /**
       * Allow us to control the ids by explicitly setting it, otherwise it will be overwritten.
       * We need this for e.g., the `ref` plugin.
       */
      reuseId: true,
    },
  }) as unknown as T

  /**
   * GH #1519 — recurrence of #1257 / #1453. Slate sizes its normalize
   * iteration cap as `initialDirtyPathsLength * 42`, computed *once* from the
   * dirty paths present when the cycle starts (see slate's `shouldNormalize`).
   * On a ChatEditor mount the loaded comment is `comment > line > text`, so a
   * force-normalize starts with ~3 dirty paths and a cap of 126.
   *
   * The line plugin's normalizer legitimately explodes a single `\n`-laden
   * text node into one `line` per source line (markdown agent replies persist
   * as a single text node with the whole answer). A ~68-newline reply inserts
   * ~68 `line` nodes in one transform; each `insert_node` dirties the node,
   * its text child and its ancestors, so the dirty-path count jumps well past
   * 126 in a single pass. That is *bounded* work proportional to content
   * length — not an infinite loop — but the cap was sized for the tiny
   * pre-split tree, so the loop throws "Could not completely normalize the
   * editor after 126 iterations" and the ChatEditor mount crashes.
   *
   * Re-base the cap on the running *peak* dirty-path count instead of the
   * initial one. Legitimate normalization always drains dirty paths back to
   * zero, so a budget that grows alongside a one-shot explosion still
   * terminates. A genuine stuck normalizer (the #1453 lift/split oscillation)
   * keeps re-dirtying the same fixed set of paths without ever growing the
   * tree, so the peak plateaus and the cap still trips — preserving slate's
   * infinite-loop guard. `withPlate` binds slate's default `shouldNormalize`,
   * so we override it here, after `withPlate`, where it survives. */
  const { shouldNormalize } = editor
  let peakDirtyPaths = 0
  editor.shouldNormalize = arg => {
    peakDirtyPaths = Math.max(peakDirtyPaths, arg.initialDirtyPathsLength, arg.dirtyPaths.length)
    return shouldNormalize({ ...arg, initialDirtyPathsLength: peakDirtyPaths })
  }

  /** Fast path: plain text nodes without newlines need only Slate's base
   *  normalization (adjacent-text merging). No plugin normalizer acts on them,
   *  so skip the entire wrapper chain (~56% of normalizeNode calls). */
  const pluginNormalizeNode = editor.normalizeNode
  editor.normalizeNode = (entry: NodeEntry<NodeIn<Value>>, options?: { operation?: Operation<Descendant> }) => {
    const [node] = entry
    if (Text.isText(node) && !node.text.includes('\n')) {
      baseNormalizeNode(entry, options)
      return
    }
    pluginNormalizeNode(entry, options)
  }

  // Install the per-editor selection store. This patches editor.onChange exactly
  // once so a single subscription drives `useNodeSelected` / `useIsInSelectionRange`
  // / `useIsSelectionCollapsed` across all blocks — replacing the per-consumer
  // `useSlateSelector` re-evaluations that caused per-keystroke O(N) block work.
  // See docs/superpowers/specs/2026-05-14-selection-subscription-perf-design.md.
  installSelectionStore(editor)

  editor.config = config
  editor.setAwareness = (awareness: unknown) => {
    editor.awareness = awareness
  }
  editor.setPluginList = (_plugins: TunaPlugin[]) => {
    /**
     * Intentionally a no-op.
     *
     * Earlier versions overwrote editor.meta.pluginList with the raw
     * TunaPlugins to keep tunasong's own consumers (block menu, search,
     * editor-commands, hotkeys) seeing the tuna shape. That clobbered the
     * resolved plugin tree withPlate had built — Plate's plugin walkers
     * (e.g. insertData → Si in @platejs/core) iterate pluginList and
     * recurse via `.plugins`, which raw tunas like `searchfolder` don't
     * have, so the walker threw `Cannot read properties of undefined
     * (reading 'plugins')` and silently aborted every paste.
     *
     * withPlate already merges every tuna's fields (node, preventBlockSplit,
     * getEditorCommands, etc.) into the resolved plugin spec it stores in
     * meta.pluginList, so tunasong readers continue to work without us
     * touching the list. Genuine runtime plugin changes require recreating
     * the editor (handled by useCreateEditor rerunning on element change).
     */
  }
  /** @todo move to using the Plate store for this */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editor.setProperty = (key: any, value: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(editor as any)[key] = value
  }

  // Track this editor in the live-editor leak gauge (GH #1666). Held value is
  // `undefined` — never the editor — so registration adds no retention.
  editorsCreated++
  editorFinalizationRegistry?.register(editor as object, undefined)

  return editor
}

// Create a new editor. It will re-create if rootElement.id or plugins change.
// When element is null, the rootElement is not set. We use this for "fake" editors.
export const useCreateEditor = (element: EntityOrElement | null, plugins: TunaPlugin[] | null) => {
  const dispatch = useThunkDispatch()
  const { getState } = useStore<RootState>()

  const { graph } = useGlobalAppContext()

  /** We allow only once creation per rootElement */
  const [rootElement, setRootElement] = useState<EntityOrElement | null>(element)

  useEffect(() => {
    if (!rootElement && element) {
      setRootElement(element)
    }
  }, [element, rootElement])

  const [maturity = 'stable'] = useUserConfig('featureMaturity')

  const editor = useMemo(() => {
    if (!plugins) {
      return null
    }
    const id = shortUuid()
    const editor = createTunaEditor(id, plugins, { dispatch, getState, graph }, maturity)

    if (isCoreElement(rootElement)) {
      editor.rootElement = rootElement
      editor.children = rootElement.children ?? []

      editor.tf.normalize({ force: true })
    }
    return editor
  }, [dispatch, getState, maturity, plugins, rootElement, graph])

  return editor
}

/**
 * Create a new editor dynamically when either the element or plugins change.
 * This is useful for cases where the editor needs to be re-created when either changes,
 * e.g., when rendering offscreen editors or previews.
 */
export const useCreateEditorDynamic = (
  element: EntityOrElement | null,
  providedPlugins: TunaPlugin[] | null = null
) => {
  const allPlugins = usePlugins('all')
  const plugins = providedPlugins ?? allPlugins
  const dispatch = useThunkDispatch()
  const { getState } = useStore<RootState>()

  const { graph } = useGlobalAppContext()

  const [editor, setEditor] = useState<TunaEditor | null>(null)

  const [maturity = 'stable'] = useUserConfig('featureMaturity')

  useEffect(() => {
    if (!plugins) {
      setEditor(null)
      return
    }
    const id = shortUuid()
    const editor = createTunaEditor(id, plugins, { dispatch, getState, graph }, maturity)

    if (isCoreElement(element)) {
      editor.rootElement = element
      editor.children = element.children ?? []

      editor.tf.normalize({ force: true })
    }
    setEditor(editor)
  }, [dispatch, element, getState, graph, maturity, plugins])

  return editor
}
